import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { X } from "lucide-react";
import CameraPlayer from "./CameraPlayer";
import {
  fetchCameraNodes,
  fetchMyCameraSessions,
  sendCameraHeartbeat,
  startCameraStream,
  stopCameraSession,
} from "../../../services/cameraV2/cameraV2Service";

const CAMERA_OPTIONS = [
  { key: "front", label: "Front Camera" },
  { key: "rear", label: "Rear Camera" },
];

const PANEL_WIDTH = 850;
const HEARTBEAT_MS = 20000;

const normalizeError = (error, fallback) =>
  error?.response?.data?.msg ||
  error?.response?.data?.error ||
  error?.data?.msg ||
  error?.message ||
  fallback;

const areStreamsEqual = (previous, next) => {
  const previousKeys = Object.keys(previous || {}).sort();
  const nextKeys = Object.keys(next || {}).sort();
  if (previousKeys.length !== nextKeys.length) return false;
  for (let index = 0; index < previousKeys.length; index += 1) {
    if (previousKeys[index] !== nextKeys[index]) return false;
    const prevStream = previous[previousKeys[index]];
    const nextStream = next[nextKeys[index]];
    if (
      prevStream?.sessionId !== nextStream?.sessionId ||
      prevStream?.playbackUrl !== nextStream?.playbackUrl
    ) {
      return false;
    }
  }
  return true;
};

const asId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const isMjpegPlaybackUrl = (url) => {
  const playbackUrl = String(url || "").trim();
  return /\.mjpg(\?|$)/i.test(playbackUrl) || /\/video\//i.test(playbackUrl);
};

const getDeviceIdentifiers = (device) =>
  [
    device?._id,
    device?.deviceId,
    device?.terminalId,
    device?.msdn,
    device?.imei,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const resolveNodeViewKey = (node) => {
  const name = String(node?.name || "").toLowerCase();
  const channelId = Number(node?.channelId || 0);

  if (name.includes("rear") || name.includes("back") || name.includes("reverse") || channelId === 2) {
    return "rear";
  }
  if (name.includes("front") || name.includes("main") || channelId === 1) {
    return "front";
  }
  return channelId > 1 ? "rear" : "front";
};

function StreamSurface({ node, streamData, device }) {
  const playbackUrl = String(streamData?.playbackUrl || "").trim();

  if (!playbackUrl) {
    return (
      <Box
        sx={{
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.82)",
          px: 3,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 500 }}>
          Stream is preparing...
        </Typography>
      </Box>
    );
  }

  if (isMjpegPlaybackUrl(playbackUrl)) {
    return (
      <Box sx={{ minHeight: 320, bgcolor: "#202020" }}>
        <img
          src={playbackUrl}
          alt={node?.name || "Camera stream"}
          style={{
            width: "100%",
            height: "100%",
            minHeight: 320,
            maxHeight: 360,
            objectFit: "cover",
            display: "block",
            background: "#202020",
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 360 }}>
      <CameraPlayer
        device={device}
        camera={{
          name: node?.name,
          cameraType: resolveNodeViewKey(node),
        }}
        cameraType={resolveNodeViewKey(node)}
        sessionId={streamData?.sessionId}
        playbackUrl={playbackUrl}
      />
    </Box>
  );
}

function CameraStage({ title, node, streamData, device, emptyText }) {
  return (
    <Box
      sx={{
        borderRadius: 0,
        overflow: "hidden",
        bgcolor: "#2b2b2b",
        minHeight: 230,
        border: "1px solid rgba(0,0,0,0.12)",
      }}
    >
      {node && streamData?.sessionId ? (
        <StreamSurface node={node} streamData={streamData} device={device} />
      ) : (
        <Box
          sx={{
            minHeight: 230,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.85)",
            px: 3,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 500 }}>{emptyText}</Typography>
        </Box>
      )}

      <Box sx={{ px: 1.5, py: 1, bgcolor: "white" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
          {title}
        </Typography>
      </Box>
    </Box>
  );
}

export default function MapCameraPopup({ open, device, onClose }) {
  const mountedRef = useRef(true);
  const [activeTab, setActiveTab] = useState("live");
  const [selectedViews, setSelectedViews] = useState({ front: true, rear: false });
  const [cameraError, setCameraError] = useState("");
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [isStartingStream, setIsStartingStream] = useState(false);
  const [matchedNodes, setMatchedNodes] = useState([]);
  const [activeStreams, setActiveStreams] = useState({});

  const nodeMap = useMemo(() => {
    const next = {};
    (matchedNodes || []).forEach((node) => {
      const key = resolveNodeViewKey(node);
      if (!next[key]) next[key] = node;
    });
    return next;
  }, [matchedNodes]);

  const activeCameraKeys = useMemo(
    () => CAMERA_OPTIONS.filter((option) => selectedViews[option.key]).map((option) => option.key),
    [selectedViews]
  );

  const stopStreams = useCallback(async (streams) => {
    await Promise.all(
      Object.values(streams || {}).map(async (stream) => {
        if (!stream?.sessionId) return;
        try {
          await stopCameraSession(stream.sessionId);
        } catch {
          // Best effort cleanup
        }
      })
    );
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setActiveTab("live");
      setSelectedViews({ front: true, rear: false });
      setCameraError("");
      setMatchedNodes([]);
      setActiveStreams((previous) => {
        stopStreams(previous);
        return {};
      });
      return;
    }
    setCameraError("");
  }, [open, stopStreams]);

  useEffect(() => {
    if (!open || !device) return;

    let cancelled = false;

    const loadNodes = async () => {
      setIsLoadingNodes(true);
      setCameraError("");

      try {
        const identifiers = getDeviceIdentifiers(device);
        const nodes = await fetchCameraNodes();

        const filtered = (nodes || []).filter((node) => {
          const nodeDeviceId = asId(node?.deviceId);
          const nodeTerminalId = String(node?.terminalId || "").trim();
          return identifiers.includes(nodeDeviceId) || (nodeTerminalId && identifiers.includes(nodeTerminalId));
        });

        if (cancelled) return;

        setMatchedNodes(filtered);

        const hasFront = filtered.some((node) => resolveNodeViewKey(node) === "front");
        const hasRear = filtered.some((node) => resolveNodeViewKey(node) === "rear");

        setSelectedViews({
          front: hasFront || !hasRear,
          rear: hasRear && !hasFront,
        });

        if (!filtered.length) {
          setCameraError("No camera for this device");
        }
      } catch (error) {
        if (cancelled) return;
        setMatchedNodes([]);
        setCameraError(normalizeError(error, "Failed to load camera details"));
      } finally {
        if (!cancelled) setIsLoadingNodes(false);
      }
    };

    loadNodes();

    return () => {
      cancelled = true;
    };
  }, [device, open]);

  useEffect(() => {
    if (!open || !device || !matchedNodes.length) return;

    let cancelled = false;

    const syncStreams = async () => {
      if (activeTab === "history") {
        setCameraError("Camera history is not configured for this device");
        return;
      }

      setIsStartingStream(true);
      setCameraError("");

      try {
        const existingSessions = await fetchMyCameraSessions();
        const sessionsByNode = {};
        (existingSessions || []).forEach((session) => {
          const nodeId = asId(session?.cameraNodeId);
          if (nodeId) sessionsByNode[nodeId] = session;
        });

        const nextStreams = {};

        for (const cameraKey of activeCameraKeys) {
          const node = nodeMap[cameraKey];
          if (!node) continue;

          const nodeId = asId(node?._id);
          let session = sessionsByNode[nodeId];

          if (!session?.sessionId) {
            const result = await startCameraStream(nodeId);
            session = result?.session || result;
          }

          if (session?.sessionId) {
            nextStreams[cameraKey] = {
              sessionId: session.sessionId,
              playbackUrl: session.playbackUrl,
              nodeId,
            };
          }
        }

        for (const [cameraKey, previousStream] of Object.entries(activeStreams)) {
          if (nextStreams[cameraKey]?.sessionId === previousStream?.sessionId) continue;
          if (!previousStream?.sessionId) continue;
          try {
            await stopCameraSession(previousStream.sessionId);
          } catch {
            // Ignore stale session stop failure
          }
        }

        if (!cancelled && mountedRef.current) {
          setActiveStreams((previous) =>
            areStreamsEqual(previous, nextStreams) ? previous : nextStreams
          );
        }
      } catch (error) {
        if (!cancelled) {
          setCameraError(normalizeError(error, "Unable to start camera stream"));
        }
      } finally {
        if (!cancelled) setIsStartingStream(false);
      }
    };

    syncStreams();

    return () => {
      cancelled = true;
    };
  }, [activeCameraKeys, activeStreams, activeTab, device, matchedNodes.length, nodeMap, open]);

  useEffect(() => {
    if (!open || !Object.keys(activeStreams).length) return undefined;

    const interval = setInterval(() => {
      Object.values(activeStreams).forEach((stream) => {
        if (!stream?.sessionId) return;
        sendCameraHeartbeat(stream.sessionId).catch(() => {});
      });
    }, HEARTBEAT_MS);

    return () => clearInterval(interval);
  }, [activeStreams, open]);

  const handleToggleCamera = (cameraKey) => {
    if (!nodeMap[cameraKey]) return;

    setSelectedViews((previous) => {
      const next = { ...previous, [cameraKey]: !previous[cameraKey] };
      if (!next.front && !next.rear) {
        next[cameraKey] = true;
      }
      return next;
    });
  };

  const liveLabel = device?.name || device?.vehicleNumber || device?.imei || "Device";
  const visibleStages = CAMERA_OPTIONS.filter(
    (option) => selectedViews[option.key] && nodeMap[option.key]
  );
  const popupHeight = visibleStages.length > 1 ? 700 : 540;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: `min(${PANEL_WIDTH}px, calc(100vw - 72px))`,
          minHeight: popupHeight,
          borderRadius: "16px",
          overflow: "hidden",
          alignSelf: "flex-start",
          mt: "104px",
          ml: { xs: 1, md: 4 },
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "transparent",
        },
      }}
    >
      <Box sx={{ bgcolor: "white" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3.5, py: 2.5 }}
        >
          <Typography sx={{ fontSize: 28, fontWeight: 500, color: "#111827" }}>
            Live Stream For {liveLabel}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#111827" }}>
            <X size={20} />
          </IconButton>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, nextValue) => setActiveTab(nextValue)}
          variant="fullWidth"
          sx={{
            px: 4,
            minHeight: 70,
            "& .MuiTab-root": {
              minHeight: 70,
              fontSize: 30,
              fontWeight: 500,
              textTransform: "none",
              color: "#111827",
            },
            "& .MuiTabs-indicator": {
              height: 6,
              width: 90,
              marginLeft: "calc(25% - 45px)",
              borderRadius: 999,
              backgroundColor: "#4f83ff",
            },
          }}
        >
          <Tab disableRipple value="live" label="Live" />
          <Tab disableRipple value="history" label="History" />
        </Tabs>

        <Divider />

        <Box sx={{ px: 3.5, py: 3 }}>
          {(cameraError || isLoadingNodes || isStartingStream) && (
            <Alert
              severity={cameraError ? "warning" : "info"}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {cameraError ||
                (isLoadingNodes
                  ? "Checking cameras for this device..."
                  : "Starting camera stream...")}
            </Alert>
          )}

          <Stack spacing={2.5}>
            {visibleStages.map((option) => (
              <CameraStage
                key={`${activeTab}-${option.key}`}
                title={option.label}
                node={nodeMap[option.key]}
                streamData={activeStreams[option.key]}
                device={device}
                emptyText={
                  activeTab === "history"
                    ? "No video history available for this camera"
                    : `Loading ${option.label.toLowerCase()}...`
                }
              />
            ))}

            {!visibleStages.length && !isLoadingNodes && (
              <Box
                sx={{
                  minHeight: 240,
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f9fafb",
                  px: 3,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: 22, color: "#111827" }}>
                  No camera for this device
                </Typography>
              </Box>
            )}
          </Stack>

          <Stack
            direction="row"
            spacing={3.5}
            alignItems="center"
            sx={{ pt: 3, flexWrap: "wrap" }}
          >
            {CAMERA_OPTIONS.map((option) => (
              <Stack
                key={option.key}
                direction="row"
                alignItems="center"
                spacing={1.2}
                sx={{ opacity: nodeMap[option.key] ? 1 : 0.45 }}
              >
                <Checkbox
                  checked={Boolean(selectedViews[option.key] && nodeMap[option.key])}
                  onChange={() => handleToggleCamera(option.key)}
                  disabled={!nodeMap[option.key]}
                  sx={{
                    p: 0,
                    "& .MuiSvgIcon-root": {
                      fontSize: 30,
                    },
                  }}
                />
                <Typography sx={{ fontSize: 28, color: "#111827" }}>
                  {option.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}
